import {
  ComponentRef,
  Directive,
  HostListener,
  Input,
  Renderer2,
  ViewContainerRef,
} from '@angular/core';
import { filter, tap } from 'rxjs/operators';
import { FormControl } from '@angular/forms';

import { GasDynamicComponentsService } from '@cikrf/gas-utils/services';

import { GasDatepickerComponent } from '../../forms/gas-datepicker/gas-datepicker.component';
import { GasInputComponent } from '../../forms/gas-input/input/gas-input.component';
import { GasDatepickerHelper } from '../../forms/gas-datepicker/gas-datepicker-helper';
import * as datepickerHelper from './datepicker.helper';
import { GasInputBoolean } from '@cikrf/gas-utils/decorators';
import { GasDatepickerTime } from '../../forms/gas-datepicker';
import { DatePipe } from '@angular/common';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

const DATEPICKER_POSITION = {
  offsetTop: 0,
  inputHeight: 30,
};

@UntilDestroy()
@Directive({
  selector: '[gasDatepicker]',
})
/** @deprecated */
export class GasDatepickerDirective {
  @Input()
  public dateFormat: string;

  @Input()
  public presetDate: Date = null;

  @GasInputBoolean()
  @Input()
  public nativeDisabled = false;

  @GasInputBoolean()
  @Input()
  public timesPicker = false;

  @Input()
  public defaultTime: GasDatepickerTime = {
    hours: 0,
    minutes: 0,
    seconds: 0,
  };

  @Input()
  public minDate: Date;

  @Input()
  public maxDate: Date;

  public datepickerComponent: ComponentRef<GasDatepickerComponent>;
  public isOpen = false;

  public intersectionObserverInit: IntersectionObserverInit = {
    root: null,
    rootMargin: '0px',
    threshold: 0.1,
  };

  private gasDatepickerHelper: GasDatepickerHelper;

  private get control(): FormControl {
    return this.gasInput.form;
  }

  constructor(
    // Public access modifier for GasRangeComponent
    public gasInput: GasInputComponent,
    private viewContainerRef: ViewContainerRef,
    private gasDynamicComponentsService: GasDynamicComponentsService,
    private renderer: Renderer2,
    private datePipe: DatePipe,
  ) {
  }

  @HostListener('click', ['$event'])
  public clickHandler(event: Event): void {
    event.stopPropagation();
    const targetIndex = event.composedPath().indexOf(this.datepickerComponent?.location.nativeElement);

    if (this.control.disabled || this.nativeDisabled || targetIndex !== -1) {
      this.isOpen = false;
      return;
    }

    if (this.isOpen) {
      this.removeComponent();
    } else {
      this.createComponent();
      this.setDatepickerPosition();
    }
  }

  public createComponent(): void {
    datepickerHelper.removeDuble();

    this.datepickerComponent = this.gasDynamicComponentsService
      .createChildComponent(GasDatepickerComponent, this.viewContainerRef);

    this.setComponentProps();

    this.presetDate = this.getPresetDate() || this.presetDate;
    this.datepickerComponent.instance.presetDate = this.presetDate;

    this.gasDatepickerHelper = this.datepickerComponent?.instance?.gasDatepickerHelper;

    this.addSubsribes();

    this.isOpen = true;
  }

  public removeComponent(): void {
    this.gasDynamicComponentsService.deleteComponent(this.datepickerComponent);
    this.datepickerComponent = null;
    this.isOpen = false;
  }

  /** позиционируем календарь в зависимости от положения на странице элемента триггера */
  private setDatepickerPosition(): void {
    if (!this.datepickerComponent) {
      return;
    }

    const datepickerRef = this.datepickerComponent.instance.datepickerWrapper.nativeElement;
    const datapickerCoords = datepickerRef.getBoundingClientRect();
    const pageHeight = window.innerHeight;
    const pageWidth = window.innerWidth;

    /** помещаем датапикер над элементом если не помещается снизу */
    if (pageHeight - datapickerCoords.bottom < 0) {
      this.renderer.setStyle(datepickerRef, 'top', `inherit`);
      this.renderer.setStyle(datepickerRef, 'bottom', `${DATEPICKER_POSITION.inputHeight}px`);
    } else {
      this.renderer.setStyle(datepickerRef, 'top', `${DATEPICKER_POSITION.offsetTop}px`);
    }

    if (pageWidth - datapickerCoords.right < 0) {
      this.renderer.setStyle(datepickerRef, 'left', `inherit`);
      this.renderer.setStyle(datepickerRef, 'right', '0');
    } else {
      this.renderer.setStyle(datepickerRef, 'left', '0');
    }
  }

  private getPresetDate(): Date {
    if (this.control?.value) {
      if (typeof this.control.value === 'string' && datepickerHelper.gasCheckDate(this.control.value)) {

        // todo: нужно разобраться с установкой значения датапикера и выпилить костыли
        const rabbishThatNeedsToBeRefactoredAsSoonAsPossible = this.control.value.split(' ').length;
        const date = rabbishThatNeedsToBeRefactoredAsSoonAsPossible === 1
          ? this.transformDateToCleanIso(this.control.value)
          : this.transformDateTimeToCleanIso(this.control.value);

        return new Date(date);
      } else {
        return datepickerHelper.gasCheckDate(this.control.value) ? this.control.value : null;
      }
    }

    return null;
  }

  // todo: стырено с pvv, надо бы убрать это при рефакторе метода getPresetDate
  private transformDateToCleanIso(date: string | Date): string | undefined {
    if (typeof date === 'string') {
      // данные приходят в формате dd.MM.yyyy
      const dateValue = date.split('.').reverse().join('-');
      date = new Date(dateValue) as any;
    }
    return this.datePipe.transform(new Date(date as string), 'yyyy-MM-dd') || undefined;
  }

  // todo: стырено с pvv, надо бы убрать это при рефакторе метода getPresetDate
  private transformDateTimeToCleanIso(date: string): string | undefined {
    if (typeof date === 'string') {
      // данные приходят в формате dd.MM.yyyy HH:mm
      const dateValue = date.split(' ')[0].split('.').reverse().join('-');
      const dateTime = `T${date.split(' ')[1]}`;
      date = new Date(`${dateValue}${dateTime}`) as any;
    }
    return this.datePipe.transform(new Date(date as string), 'yyyy-MM-ddTHH:mm:ss') || undefined;
  }

  private setComponentProps(): void {
    this.datepickerComponent.instance.minDate = this.minDate;
    this.datepickerComponent.instance.maxDate = this.maxDate;
    this.datepickerComponent.instance.timesPicker = this.timesPicker;
    this.datepickerComponent.instance.defaultTime = this.defaultTime;
  }

  private addSubsribes(): void {
    const observer = new IntersectionObserver((entries: IntersectionObserverEntry[]) => {
      if (!entries[0].isIntersecting) {
        this.removeComponent();
      }
    }, this.intersectionObserverInit);
    observer.observe(this.datepickerComponent.instance.datepickerWrapper.nativeElement);

    this.datepickerComponent.instance.selectedDateSubject$
      .pipe(
        tap((date: Date) => {
          this.gasDatepickerHelper.actualDate$.next(this.presetDate);
          if (this.presetDate !== date) {
            this.control?.setValue(date);
          }
        }),
        untilDestroyed(this),
      ).subscribe();

    this.datepickerComponent.instance.closeSubject$
      .pipe(
        filter(isClick => !!isClick),
        tap(() => {
          this.removeComponent();
          this.isOpen = true;
        }),
        untilDestroyed(this),
      ).subscribe();

    this.control?.valueChanges.pipe(
      untilDestroyed(this),
    ).subscribe(() => {
      const date = this.gasInput?.ngControl?.control?.value;
      const currentDate = this.datepickerComponent?.instance?.presetDate;

      if (typeof date === 'object' && date?.getTime() !== currentDate?.getTime()) {
        this.datepickerComponent.instance.presetDate = date;
      }
    });
  }
}
